import React, { useState, useEffect } from "react";
import {
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../loader/Loader";
import { toast } from "react-toastify";

type SubmitModalProps = {
  title: string;
  children: React.ReactNode;
  asyncOperation: () => Promise<boolean>;
  closeSubmitModal: CallableFunction;
  childErrorMessage?: string;
  allowSubmit: boolean;
  size: false | Breakpoint | undefined;
  isLoading?: boolean;
  submitButtonName?: string;
  closeModalAfterSubmit?: boolean;
  showSubmitButton?: boolean;
};

export interface ModalTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

export const BootstrapModal = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export function BootstrapModalTitle(props: ModalTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{
        minHeight: "36px",
        m: 0,
        p: 1,
        backgroundColor: "primary.main",
        color: (theme) => theme.palette.grey[50],
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 0,
            color: (theme) => theme.palette.grey[50],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const SubmitModal = (Props: SubmitModalProps) => {
  const {
    title,
    children,
    asyncOperation,
    closeSubmitModal,
    childErrorMessage,
    allowSubmit,
    size,
    isLoading = false,
    submitButtonName = "Submit",
    closeModalAfterSubmit = true,
    showSubmitButton = true,
  } = Props;

  const [isOpen, setIsOpen] = useState(true);

  const [isOperationInProgress, setOperationInProgress] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      if (childErrorMessage) {
        toast.error(childErrorMessage);
      }
    }
    return () => {
      isFunctionRan = false;
    };
  }, [childErrorMessage]);

  const _closeSubmitModal = () => {
    setIsOpen(false);
    closeSubmitModal();
  };

  const handleClickCloseModal = () => {
    _closeSubmitModal();
  };

  const _executeOperation = async () => {
    if (childErrorMessage) {
      toast.error(childErrorMessage);
    }

    setOperationInProgress(true);
    try {
      const result = await asyncOperation();
      setOperationInProgress(false);
      if (closeModalAfterSubmit && result) {
        _closeSubmitModal();
      }
    } catch (e) {
      setOperationInProgress(false);
      toast.error(e!.toString());
    }
  };

  return (
    <BootstrapModal
      open={isOpen}
      onClose={handleClickCloseModal}
      aria-labelledby="customized-dialog-title"
      maxWidth={size}
      fullWidth
      fullScreen={fullScreen}
    >
      <BootstrapModalTitle
        id="customized-dialog-title"
        onClose={handleClickCloseModal}
      >
        <Typography>{title}</Typography>
      </BootstrapModalTitle>
      <DialogContent dividers>
        <div>{children}</div>
      </DialogContent>
      <DialogActions>
        {showSubmitButton && (
          <Button
            variant="contained"
            onClick={() => _executeOperation()}
            size="small"
            disabled={!allowSubmit}
          >
            {submitButtonName}
          </Button>
        )}
        <Button variant="outlined" onClick={handleClickCloseModal} size="small">
          Cancel
        </Button>
      </DialogActions>
      <Loader isLoading={isOperationInProgress || isLoading} />
    </BootstrapModal>
  );
};

export default SubmitModal;
